import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  axApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  CreateManufacturer,
  UpdateManufacturer,
  UpdateManufacturerStatus,
} from "./types";

export const axManufacturersApi = {
  getManuFacturerSummary: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "manufacturer-summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },
  getManuFacturer: async (
    page: number,
    remoteUtilityId: number,
    search_data?: string,
    country?: string,
    status?: boolean
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "manufacturer",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page);
        query.push("limit", PAGE_SIZE);

        if (search_data) {
          query.push("search_data", search_data);
        }
        if (country) {
          query.push("country", country);
        }
        if (status) {
          query.push("is_active", status);
        }
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  createManufacturer: async (
    data: CreateManufacturer
  ): Promise<CreateManufacturer> => {
    const payload = {
      name: data.name,
      remote_utility_id: data.remote_utility_id,
      manufacturer_contact_email: data.manufacturer_contact_email,
      manufacturer_contact_person: data.manufacturer_contact_person,
      description: data.description,
      website: data.website,
      country: data.country,
      contact_number: data.contact_number,
      category: data.category,
    };

    try {
      const response = await axApiClient.post("manufacturer/", payload);
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },
  updateManufacturer: async (
    data: UpdateManufacturer
  ): Promise<UpdateManufacturer> => {
    const payload = {
      remote_utility_id: data.remote_utility_id,
      manufacturer_contact_email: data.manufacturer_contact_email,
      manufacturer_contact_person: data.manufacturer_contact_person,
      description: data.description,
      website: data.website,
      country: data.country,
      contact_number: data.contact_number,
      category: data.category,
    };

    try {
      const response = await axApiClient.put(
        `manufacturer/${data.id}/`,
        payload
      );
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },
  updateManufacturerStatus: async (
    data: UpdateManufacturerStatus
  ): Promise<UpdateManufacturerStatus> => {
    const payload = {
      isActive: data.isActive,
    };

    try {
      const response = await axApiClient.put(
        `manufacturer/${data.id}/`,
        payload
      );
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },
  getCountry: async (
    remoteUtilityId: number,
    config_level: string,
    show_inactive: boolean
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", config_level);
        query.push("show_inactive", show_inactive);
        return query;
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },
  getAssetType: async (
    remoteUtilityId: number,
    config_level: string,
    show_inactive: boolean
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/asset_class_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", config_level);
        query.push("show_inactive", show_inactive);
        return query;
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },
  getManufacturerById: async (remoteUtilityId: number, id: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `manufacturer-detail/${id}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },

  getAllAssets: async (
    remoteUtilityId: number,
    manufacturer_id: number,
    queryParams?: Record<string, any>
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "assets",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("manufacturer_id", manufacturer_id);
        if (queryParams.page) query.push("page", queryParams.page);
        if (queryParams.limit) query.push("limit", queryParams.limit);
        if (queryParams.type) query.push("asset_type_code", queryParams.type);
        if (queryParams.status) query.push("status", queryParams.status);

        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  getAllAssetStatus: async () => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "choices/asset_status",
      () => {}
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },
};
